import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Button } from 'blaze-ui/Button'
import { useModals } from 'almanac-ui/Modal'
import styles from './Processing.module.scss'
import { useAsyncScreenshot, WebsiteBlockedError } from 'hooks/queries/useAsyncScreenshot'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Close } from 'blaze-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface ProcessingProps {
  onCancel?: () => void
  websiteUrl?: string
  initialScreenshotUrl?: string
  abortController?: React.MutableRefObject<AbortController>
  showCancel?: boolean
  steps?: { name: string; minDuration: number; maxDuration: number }[]
  cancelOnError?: boolean
}

// Single variable to control the overall timing (in milliseconds)
// Increase to slow down, decrease to speed up
const TOTAL_ANIMATION_DURATION = 12000 // 12 seconds total animation time
const FIRST_STEP_TIMEOUT = 30000 // 30 seconds timeout for the first step

// Base durations for steps (will be scaled by the TOTAL_ANIMATION_DURATION)
const baseSteps = [
  { name: 'Analyzing your webpage', minDurationRatio: 0.15, maxDurationRatio: 0.3 },
  { name: 'Pulling colors', minDurationRatio: 0.12, maxDurationRatio: 0.22 },
  { name: 'Scraping fonts', minDurationRatio: 0.14, maxDurationRatio: 0.23 },
  { name: 'Searching for a voice', minDurationRatio: 0.13, maxDurationRatio: 0.24 },
  { name: 'Creating styles', minDurationRatio: 0.15, maxDurationRatio: 0.26 },
  { name: 'Pulling photos', minDurationRatio: 0.12, maxDurationRatio: 0.22 },
]

// Calculate actual durations based on the TOTAL_ANIMATION_DURATION
const defaultSteps = baseSteps.map((step) => ({
  name: step.name,
  minDuration: Math.floor(step.minDurationRatio * TOTAL_ANIMATION_DURATION),
  maxDuration: Math.floor(step.maxDurationRatio * TOTAL_ANIMATION_DURATION),
}))

// Calculate speed factors based on TOTAL_ANIMATION_DURATION
// These values inversely scale with the total duration to maintain consistent behavior
const getSpeedFactors = () => {
  const baseAnimationDuration = 15000 // The original reference duration for which default values were set
  const speedFactor = baseAnimationDuration / TOTAL_ANIMATION_DURATION

  return {
    incrementMultiplier: Math.max(1, Math.round(speedFactor * 1.5)), // Increase increments for faster animations
    intervalReduction: Math.min(1, 0.7 + speedFactor * 0.3), // Reduce intervals for faster animations
    transitionDelay: Math.max(200, Math.min(400, Math.floor(400 / speedFactor))), // Between 200-400ms
  }
}

export const Processing = ({
  onCancel: propsOnCancel,
  websiteUrl,
  abortController,
  showCancel = true,
  steps = defaultSteps,
  initialScreenshotUrl = '',
  cancelOnError = true,
}: ProcessingProps) => {
  const { closeModal } = useModals()
  const onCancel = useMemo(() => {
    return propsOnCancel ? propsOnCancel : () => closeModal()
  }, [propsOnCancel, closeModal])
  const [screenshotUrl, setScreenshotUrl] = useState<string | null>(initialScreenshotUrl)
  const [error, setError] = useState(false)
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [currentStepPercentage, setCurrentStepPercentage] = useState(0)
  const [stepComplete, setStepComplete] = useState(false)
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null)
  const stepTimerRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null) // Ref for the 30-second timeout
  const { isMobile } = useResponsiveDesign()
  const speedFactors = getSpeedFactors()
  const stepsLength = steps.length

  const { createScreenshot, isLoading: isMutating } = useAsyncScreenshot()

  const getRandomIncrement = () => (Math.floor(Math.random() * 3) + 1) * speedFactors.incrementMultiplier

  const getRandomTimeout = (min: number, maxExtra: number) => {
    return Math.floor(Math.random() * maxExtra) + min
  }

  useEffect(() => {
    if (initialScreenshotUrl && !screenshotUrl) {
      setScreenshotUrl(initialScreenshotUrl)
      return
    }

    if (screenshotUrl || isMutating) {
      return
    }

    const fetchScreenshot = async () => {
      if (websiteUrl) {
        try {
          // Set a 30-second timeout for the first step
          if (timeoutRef.current === null && currentStepIndex === 0) {
            timeoutRef.current = setTimeout(() => {
              if (!screenshotUrl && !error && cancelOnError) {
                onCancel()
              }
            }, FIRST_STEP_TIMEOUT)
          }

          const result = await createScreenshot(
            websiteUrl,
            abortController?.current.signal || new AbortController().signal,
          )

          // Clear the timeout if we successfully got a screenshot
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }

          if (result) {
            setScreenshotUrl(result)
          } else {
            setError(true)
          }
        } catch (err) {
          if (err instanceof WebsiteBlockedError && cancelOnError) {
            onCancel()
          }
          // Clear the timeout if there was an error
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
            timeoutRef.current = null
          }
          setError(true)
        }
      }
    }

    fetchScreenshot()

    // Clean up the timeout if the component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [
    websiteUrl,
    createScreenshot,
    abortController,
    onCancel,
    currentStepIndex,
    screenshotUrl,
    error,
    isMutating,
    initialScreenshotUrl,
    cancelOnError,
  ])

  // Clean up timers
  const cleanupTimers = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current)
      progressTimerRef.current = null
    }

    if (stepTimerRef.current) {
      clearTimeout(stepTimerRef.current)
      stepTimerRef.current = null
    }
  }

  const moveToNextStep = useCallback(() => {
    cleanupTimers()

    setTimeout(() => {
      if (currentStepIndex < stepsLength - 1) {
        setCurrentStepIndex((prev) => prev + 1)
        setCurrentStepPercentage(0)
        setStepComplete(false)
      }
    }, speedFactors.transitionDelay)
  }, [currentStepIndex, speedFactors.transitionDelay, stepsLength])

  // Effect to start the progress when the step changes or when the screenshot loads for the first step
  useEffect(() => {
    const startProgress = () => {
      cleanupTimers()

      const currentStep = steps[currentStepIndex]

      // First step: Don't complete until screenshot is available
      if (currentStepIndex === 0) {
        if (screenshotUrl) {
          // Screenshot is available, finish this step
          progressTimerRef.current = setInterval(() => {
            setCurrentStepPercentage((prev) => {
              const increment = getRandomIncrement() * 2
              const nextValue = Math.min(prev + increment, 100)

              if (nextValue === 100 && !stepComplete) {
                setStepComplete(true)
                moveToNextStep()
              }

              return nextValue
            })
          }, 70 * speedFactors.intervalReduction)
        } else if (!error) {
          // No screenshot yet, progress very slowly up to 90%
          progressTimerRef.current = setInterval(() => {
            setCurrentStepPercentage((prev) => {
              // Smaller increments while waiting
              const increment = Math.max(1, getRandomIncrement() - 1)
              return Math.min(prev + increment, 90)
            })
          }, (200 + Math.random() * 100) * speedFactors.intervalReduction)
        } else {
          // Error occurred, move to next step anyway
          progressTimerRef.current = setInterval(() => {
            setCurrentStepPercentage((prev) => {
              const nextValue = Math.min(prev + getRandomIncrement(), 100)

              if (nextValue === 100 && !stepComplete) {
                setStepComplete(true)
                moveToNextStep()
              }

              return nextValue
            })
          }, 80 * speedFactors.intervalReduction)
        }
      }
      // Last step: Stop at 95%
      else if (currentStepIndex === stepsLength - 1) {
        progressTimerRef.current = setInterval(() => {
          setCurrentStepPercentage((prev) => {
            const increment = getRandomIncrement()
            const nextValue = Math.min(prev + increment, 95)

            if (nextValue === 95) {
              cleanupTimers()
            }

            return nextValue
          })
        }, (150 + Math.random() * 150) * speedFactors.intervalReduction)
      }
      // All other steps: progress naturally to 100% with variable speed
      else {
        const { minDuration, maxDuration } = currentStep
        const totalDuration = getRandomTimeout(minDuration, maxDuration - minDuration)
        // Scale the interval based on the speed factor
        const avgInterval = (totalDuration / (100 / getRandomIncrement())) * 1.2 * speedFactors.intervalReduction

        progressTimerRef.current = setInterval(() => {
          setCurrentStepPercentage((prev) => {
            // Add some variability to the increments
            const baseIncrement = getRandomIncrement()
            const variableIncrement =
              Math.random() > 0.75 // Less frequent jumps
                ? baseIncrement * 2 // Occasional faster jumps
                : baseIncrement

            const nextValue = Math.min(prev + variableIncrement, 100)

            if (nextValue === 100 && !stepComplete) {
              setStepComplete(true)
              moveToNextStep()
            }

            return nextValue
          })
        }, avgInterval * (0.9 + Math.random() * 0.3))
      }
    }
    // Special case for the first step - restart progress if screenshot becomes available
    if (currentStepIndex === 0 && screenshotUrl) {
      cleanupTimers()
      setCurrentStepPercentage((prev) => Math.min(prev, 90)) // Ensure we're not already at 100%
    }

    startProgress()

    return cleanupTimers
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, screenshotUrl, error, moveToNextStep, stepComplete, speedFactors.intervalReduction])

  // Additional cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      cleanupTimers()
    }
  }, [])

  const renderPreviewContent = () => {
    if (screenshotUrl) {
      return <img src={screenshotUrl} alt="Website Preview" className={styles.websiteScreenshot} />
    }

    if (error) {
      return (
        <div className={styles.fallbackContainer}>
          <div className={styles.fallbackIcon}>
            <Close size={50} color="var(--dark-80)" />
          </div>
        </div>
      )
    }

    // Loading state
    return (
      <LoadingShimmerer>
        <div className={styles.loadingPlaceholder} />
      </LoadingShimmerer>
    )
  }

  return (
    <div className={styles.container} data-testid="processing-container">
      {/* Website screenshot */}
      <div className={styles.previewContainer}>{renderPreviewContent()}</div>

      {/* Current step with percentage and fixed-width animated ellipsis */}
      <div className={styles.progressSection}>
        <div className={styles.stepsContainer}>
          {steps.map((step, index) => (
            <Heading level={4} key={index} color={currentStepIndex === index ? 'var(--dark-80)' : 'var(--dark-40)'}>
              <span style={{ display: 'inline-block', minWidth: '100%' }}>
                {step.name}
                {currentStepIndex === index && `... ${currentStepPercentage}%`}
              </span>
            </Heading>
          ))}
        </div>
      </div>

      {showCancel && (
        <div className={styles.buttonContainer}>
          <Button variant="tertiary" onPress={onCancel} size="lg" fullWidth={isMobile}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  )
}
